import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  HiArrowLeft, 
  HiClock, 
  HiFlag, 
  HiUser, 
  HiFolder,
  HiPencil,
  HiTrash,
  HiChat,
  HiCheckCircle,
  HiExclamationCircle,
  HiPlus,
  HiCheck,
  HiX,
  HiChevronDown,
  HiCalendar,
  HiClipboardList,
  HiLockClosed,
  HiArrowRight,
} from 'react-icons/hi'
import { format, isPast, formatDistanceToNow } from 'date-fns'
import { tasksApi, projectsApi, authApi } from '../api/client'
import { useAuth } from '../hooks/useAuth'

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do', color: 'bg-gray-200', textColor: 'text-gray-700', dotColor: 'bg-gray-400' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100', textColor: 'text-blue-700', dotColor: 'bg-blue-500' },
  { value: 'review', label: 'Review', color: 'bg-amber-100', textColor: 'text-amber-700', dotColor: 'bg-amber-500' },
  { value: 'done', label: 'Done', color: 'bg-green-100', textColor: 'text-green-700', dotColor: 'bg-green-500' },
  { value: 'blocked', label: 'Blocked', color: 'bg-red-100', textColor: 'text-red-700', dotColor: 'bg-red-500' },
]

const PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical', icon: '🔴' },
  { value: 'high', label: 'High', icon: '🟠' },
  { value: 'medium', label: 'Medium', icon: '🟡' },
  { value: 'low', label: 'Low', icon: '🟢' },
]

export default function TaskPage() {
  const { taskId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  
  const [task, setTask] = useState(null)
  const [project, setProject] = useState(null)
  const [activities, setActivities] = useState([])
  const [orgUsers, setOrgUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // Inline editing states
  const [editingTitle, setEditingTitle] = useState(false)
  const [editingDescription, setEditingDescription] = useState(false)
  const [titleValue, setTitleValue] = useState('')
  const [descValue, setDescValue] = useState('')
  const [newComment, setNewComment] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [showStatusMenu, setShowStatusMenu] = useState(false)
  const [showPriorityMenu, setShowPriorityMenu] = useState(false)
  const [showAssigneeMenu, setShowAssigneeMenu] = useState(false)
  const [editingDueDate, setEditingDueDate] = useState(false)
  const [dueDateValue, setDueDateValue] = useState('')
  
  // Subtask creation
  const [showAddSubtask, setShowAddSubtask] = useState(false)
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('')
  
  // Dependency management
  const [showAddDependency, setShowAddDependency] = useState(false)
  const [depSearchQuery, setDepSearchQuery] = useState('')
  const [depSearchResults, setDepSearchResults] = useState([])
  const [depSearching, setDepSearching] = useState(false)
  
  const titleRef = useRef(null)
  const descRef = useRef(null)

  useEffect(() => {
    async function fetchTask() {
      try {
        setLoading(true)
        const [taskRes, usersRes] = await Promise.all([
          tasksApi.get(taskId),
          authApi.listUsers().catch(() => ({ data: [] })),
        ])
        const taskData = taskRes.data
        setTask(taskData)
        setTitleValue(taskData.title)
        setDescValue(taskData.description || '')
        setDueDateValue(taskData.due_date ? taskData.due_date.split('T')[0] : '')
        setOrgUsers(Array.isArray(usersRes.data) ? usersRes.data : [])
        
        // Fetch project info
        if (taskData.project_id) {
          try {
            const projRes = await projectsApi.get(taskData.project_id)
            setProject(projRes.data)
          } catch (e) {
            console.warn('Could not fetch project:', e)
          }
        }
        
        // Fetch activities separately so a failure doesn't break the page
        try {
          const activityRes = await tasksApi.getActivities(taskId)
          setActivities(Array.isArray(activityRes.data) ? activityRes.data : [])
        } catch (e) {
          console.warn('Could not fetch activities:', e)
          setActivities([])
        }
      } catch (err) {
        console.error('Failed to fetch task:', err)
        setError(err.response?.status === 404 ? 'Task not found' : 'Failed to load task')
      } finally {
        setLoading(false)
      }
    }
    fetchTask()
  }, [taskId])

  // Focus handlers for inline editing
  useEffect(() => {
    if (editingTitle && titleRef.current) {
      titleRef.current.focus()
      titleRef.current.select()
    }
  }, [editingTitle])

  useEffect(() => {
    if (editingDescription && descRef.current) {
      descRef.current.focus()
    }
  }, [editingDescription])

  const saveField = async (field, value) => {
    try {
      const updateData = { [field]: value }
      const res = await tasksApi.update(taskId, updateData)
      setTask(prev => ({ ...prev, ...res.data }))
      refreshActivities()
    } catch (err) {
      console.error(`Failed to update ${field}:`, err)
    }
  }

  const handleTitleSave = () => {
    if (titleValue.trim() && titleValue !== task.title) {
      saveField('title', titleValue.trim())
    } else {
      setTitleValue(task.title)
    }
    setEditingTitle(false)
  }

  const handleDescSave = () => {
    if (descValue !== (task.description || '')) {
      saveField('description', descValue || null)
    }
    setEditingDescription(false)
  }

  const handleStatusChange = async (newStatus) => {
    setShowStatusMenu(false)
    if (newStatus === task.status) return
    setTask(prev => ({ ...prev, status: newStatus }))
    try {
      const res = await tasksApi.update(taskId, { status: newStatus })
      setTask(prev => ({ ...prev, ...res.data }))
      refreshActivities()
    } catch (err) {
      console.error('Failed to update status:', err)
      const taskRes = await tasksApi.get(taskId)
      setTask(taskRes.data)
    }
  }

  const handlePriorityChange = async (newPriority) => {
    setShowPriorityMenu(false)
    if (newPriority === task.priority) return
    setTask(prev => ({ ...prev, priority: newPriority }))
    try {
      const res = await tasksApi.update(taskId, { priority: newPriority })
      setTask(prev => ({ ...prev, ...res.data }))
      refreshActivities()
    } catch (err) {
      console.error('Failed to update priority:', err)
      const taskRes = await tasksApi.get(taskId)
      setTask(taskRes.data)
    }
  }

  const handleAssigneeChange = async (userId) => {
    setShowAssigneeMenu(false)
    if (userId === task.assignee_id) return
    // Optimistic update with user info from orgUsers
    const assignedUser = userId ? orgUsers.find(u => u.id === userId) : null
    setTask(prev => ({
      ...prev,
      assignee_id: userId || null,
      assignee: assignedUser ? { id: assignedUser.id, display_name: assignedUser.display_name, email: assignedUser.email, avatar_url: assignedUser.avatar_url } : null,
    }))
    try {
      const res = await tasksApi.update(taskId, { assignee_id: userId || null })
      setTask(prev => ({ ...prev, ...res.data }))
      refreshActivities()
    } catch (err) {
      console.error('Failed to update assignee:', err)
      // Re-fetch on error to revert
      const taskRes = await tasksApi.get(taskId)
      setTask(taskRes.data)
    }
  }

  const handleDueDateSave = async () => {
    setEditingDueDate(false)
    const newValue = dueDateValue ? new Date(dueDateValue + 'T00:00:00').toISOString() : null
    const oldValue = task.due_date ? task.due_date.split('T')[0] : ''
    if (dueDateValue !== oldValue) {
      setTask(prev => ({ ...prev, due_date: newValue }))
      try {
        const res = await tasksApi.update(taskId, { due_date: newValue })
        setTask(prev => ({ ...prev, ...res.data }))
        refreshActivities()
      } catch (err) {
        console.error('Failed to update due date:', err)
        const taskRes = await tasksApi.get(taskId)
        setTask(taskRes.data)
        setDueDateValue(taskRes.data.due_date ? taskRes.data.due_date.split('T')[0] : '')
      }
    }
  }

  const handleAddComment = async (e) => {
    e.preventDefault()
    if (!newComment.trim()) return
    
    setSubmittingComment(true)
    try {
      await tasksApi.addComment(taskId, newComment.trim())
      setNewComment('')
      refreshActivities()
    } catch (err) {
      console.error('Failed to add comment:', err)
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleCreateSubtask = async () => {
    if (!newSubtaskTitle.trim()) return
    try {
      await tasksApi.create({
        title: newSubtaskTitle.trim(),
        project_id: task.project_id,
        board_id: task.board_id,
        column_id: task.column_id,
        parent_task_id: task.id,
      })
      setNewSubtaskTitle('')
      setShowAddSubtask(false)
      // Re-fetch task to get updated subtasks
      const taskRes = await tasksApi.get(taskId)
      setTask(taskRes.data)
    } catch (err) {
      console.error('Failed to create subtask:', err)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) return
    try {
      await tasksApi.delete(taskId)
      navigate(-1)
    } catch (err) {
      console.error('Failed to delete task:', err)
    }
  }

  // Dependency handlers
  const handleSearchDependency = async (query) => {
    setDepSearchQuery(query)
    if (!query.trim() || query.trim().length < 2) {
      setDepSearchResults([])
      return
    }
    setDepSearching(true)
    try {
      const res = await tasksApi.list({ search: query.trim(), project_id: task.project_id, limit: 10 })
      const results = (res.data || []).filter(t => 
        t.id !== taskId && 
        !(task.dependencies || []).some(d => d.depends_on_task_id === t.id)
      )
      setDepSearchResults(results)
    } catch (err) {
      console.error('Failed to search tasks:', err)
    } finally {
      setDepSearching(false)
    }
  }

  const handleAddDependency = async (dependsOnTaskId) => {
    try {
      await tasksApi.addDependency(taskId, dependsOnTaskId)
      // Re-fetch task to get updated dependencies
      const taskRes = await tasksApi.get(taskId)
      setTask(taskRes.data)
      setDepSearchQuery('')
      setDepSearchResults([])
      setShowAddDependency(false)
      refreshActivities()
    } catch (err) {
      console.error('Failed to add dependency:', err)
      alert(err.response?.data?.detail || 'Failed to add dependency')
    }
  }

  const handleRemoveDependency = async (dependencyId) => {
    try {
      await tasksApi.removeDependency(taskId, dependencyId)
      const taskRes = await tasksApi.get(taskId)
      setTask(taskRes.data)
      refreshActivities()
    } catch (err) {
      console.error('Failed to remove dependency:', err)
    }
  }

  const refreshActivities = async () => {
    try {
      const res = await tasksApi.getActivities(taskId)
      setActivities(Array.isArray(res.data) ? res.data : [])
    } catch (_) { /* silently fail */ }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent"></div>
      </div>
    )
  }

  if (error || !task) {
    return (
      <div className="text-center py-16">
        <HiExclamationCircle className="w-16 h-16 text-surface-300 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-surface-700 mb-2">{error || 'Task not found'}</h2>
        <p className="text-surface-500 mb-6">The task you&apos;re looking for doesn&apos;t exist or you don&apos;t have access.</p>
        <button onClick={() => navigate(-1)} className="btn btn-primary">
          <HiArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </button>
      </div>
    )
  }

  const isOverdue = task.due_date && isPast(new Date(task.due_date)) && task.status !== 'done'
  const statusInfo = STATUS_OPTIONS.find(s => s.value === task.status) || STATUS_OPTIONS[0]
  const priorityInfo = PRIORITY_OPTIONS.find(p => p.value === task.priority) || PRIORITY_OPTIONS[2]
  const subtasks = task.subtasks || []
  const completedSubtasks = subtasks.filter(s => s.status === 'done')

  return (
    <div className="max-w-5xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm mb-4">
        <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-surface-100 rounded-lg transition-colors">
          <HiArrowLeft className="w-4 h-4 text-surface-500" />
        </button>
        {project && (
          <>
            <Link to={`/projects/${project.id}`} className="text-surface-500 hover:text-brand-600 transition-colors">
              {project.name}
            </Link>
            <span className="text-surface-300">/</span>
          </>
        )}
        <span className="text-surface-700 font-medium truncate">{task.title}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Main Content ── */}
        <div className="lg:col-span-2 space-y-4">
          
          {/* Title — click to edit inline */}
          <div className="group">
            {editingTitle ? (
              <input
                ref={titleRef}
                type="text"
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={(e) => { if (e.key === 'Enter') handleTitleSave(); if (e.key === 'Escape') { setTitleValue(task.title); setEditingTitle(false); } }}
                className="w-full text-2xl font-bold text-surface-800 bg-transparent border-b-2 border-brand-500 outline-none py-1"
              />
            ) : (
              <h1 
                onClick={() => setEditingTitle(true)}
                className="text-2xl font-bold text-surface-800 dark:text-surface-200 py-1 cursor-text hover:bg-surface-50 dark:hover:bg-surface-700 rounded-lg px-2 -mx-2 transition-colors"
              >
                {task.title}
                <HiPencil className="w-4 h-4 text-surface-300 inline ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
              </h1>
            )}
          </div>

          {/* Status + Priority pills */}
          <div className="flex items-center gap-3 flex-wrap">
            <DropdownButton
              open={showStatusMenu} onToggle={() => setShowStatusMenu(!showStatusMenu)} onClose={() => setShowStatusMenu(false)}
              trigger={
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium cursor-pointer ${statusInfo.color} ${statusInfo.textColor}`}>
                  <span className={`w-2 h-2 rounded-full ${statusInfo.dotColor}`} />
                  {statusInfo.label}
                  <HiChevronDown className="w-3.5 h-3.5" />
                </span>
              }
              menu={STATUS_OPTIONS.map(s => (
                <button key={s.value} onClick={() => handleStatusChange(s.value)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-surface-50 dark:hover:bg-surface-700 ${task.status === s.value ? 'bg-surface-50 dark:bg-surface-700 font-medium' : ''}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${s.dotColor}`} />
                  {s.label}
                  {task.status === s.value && <HiCheck className="w-4 h-4 ml-auto text-brand-500" />}
                </button>
              ))}
            />

            <DropdownButton
              open={showPriorityMenu} onToggle={() => setShowPriorityMenu(!showPriorityMenu)} onClose={() => setShowPriorityMenu(false)}
              trigger={
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-surface-100 text-surface-700 hover:bg-surface-200 transition-colors cursor-pointer">
                  {priorityInfo.icon} {priorityInfo.label}
                  <HiChevronDown className="w-3.5 h-3.5" />
                </span>
              }
              menu={PRIORITY_OPTIONS.map(p => (
                <button key={p.value} onClick={() => handlePriorityChange(p.value)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-surface-50 dark:hover:bg-surface-700 ${task.priority === p.value ? 'bg-surface-50 dark:bg-surface-700 font-medium' : ''}`}>
                  {p.icon} {p.label}
                  {task.priority === p.value && <HiCheck className="w-4 h-4 ml-auto text-brand-500" />}
                </button>
              ))}
            />

            {isOverdue && (
              <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">Overdue</span>
            )}
          </div>

          {/* Description */}
          <div className="card p-5 group">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-surface-400 uppercase tracking-wider">Description</h3>
              {!editingDescription && (
                <button onClick={() => setEditingDescription(true)} className="text-surface-400 hover:text-surface-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  <HiPencil className="w-4 h-4" />
                </button>
              )}
            </div>
            {editingDescription ? (
              <div>
                <textarea
                  ref={descRef}
                  value={descValue}
                  onChange={(e) => setDescValue(e.target.value)}
                  placeholder="Add a more detailed description..."
                  className="w-full min-h-[120px] p-3 text-surface-700 bg-surface-50 border border-surface-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y"
                />
                <div className="flex gap-2 mt-2">
                  <button onClick={handleDescSave} className="btn btn-primary btn-sm">Save</button>
                  <button onClick={() => { setDescValue(task.description || ''); setEditingDescription(false); }} className="btn btn-secondary btn-sm">Cancel</button>
                </div>
              </div>
            ) : (
              <div onClick={() => setEditingDescription(true)} className="cursor-text min-h-[60px]">
                {task.description ? (
                  <p className="text-surface-700 whitespace-pre-wrap leading-relaxed">{task.description}</p>
                ) : (
                  <p className="text-surface-400 italic">Click to add a description...</p>
                )}
              </div>
            )}
          </div>

          {/* Subtasks */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-surface-400 uppercase tracking-wider flex items-center gap-2">
                <HiClipboardList className="w-4 h-4" />
                Subtasks
                {subtasks.length > 0 && (
                  <span className="text-xs font-normal text-surface-400">
                    ({completedSubtasks.length}/{subtasks.length})
                  </span>
                )}
              </h3>
              <button onClick={() => setShowAddSubtask(true)} className="text-sm text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
                <HiPlus className="w-4 h-4" /> Add
              </button>
            </div>

            {subtasks.length > 0 && (
              <div className="w-full h-1.5 bg-surface-100 rounded-full mb-3 overflow-hidden">
                <div className="h-full bg-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${(completedSubtasks.length / subtasks.length) * 100}%` }} />
              </div>
            )}

            <div className="space-y-1">
              {subtasks.map(sub => (
                <Link key={sub.id} to={`/tasks/${sub.id}`}
                  className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors group">
                  <span className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    sub.status === 'done' ? 'bg-green-500 border-green-500 text-white' : 'border-surface-300 group-hover:border-surface-400'
                  }`}>
                    {sub.status === 'done' && <HiCheck className="w-3 h-3" />}
                  </span>
                  <span className={`flex-1 text-sm ${sub.status === 'done' ? 'line-through text-surface-400' : 'text-surface-700'}`}>
                    {sub.title}
                  </span>
                </Link>
              ))}
              {subtasks.length === 0 && !showAddSubtask && (
                <p className="text-sm text-surface-400 text-center py-3">No subtasks yet</p>
              )}
            </div>

            {showAddSubtask && (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-surface-100">
                <input
                  type="text" value={newSubtaskTitle}
                  onChange={(e) => setNewSubtaskTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateSubtask(); if (e.key === 'Escape') { setNewSubtaskTitle(''); setShowAddSubtask(false); } }}
                  placeholder="What needs to be done?"
                  className="flex-1 text-sm px-3 py-2 border border-surface-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                  autoFocus
                />
                <button onClick={handleCreateSubtask} disabled={!newSubtaskTitle.trim()} className="btn btn-primary btn-sm">Add</button>
                <button onClick={() => { setNewSubtaskTitle(''); setShowAddSubtask(false); }} className="btn btn-secondary btn-sm"><HiX className="w-4 h-4" /></button>
              </div>
            )}
          </div>

          {/* Activity & Comments */}
          <div className="card p-5">
            <h3 className="text-xs font-semibold text-surface-400 uppercase tracking-wider flex items-center gap-2 mb-4">
              <HiChat className="w-4 h-4" />
              Activity & Comments
            </h3>
            
            <form onSubmit={handleAddComment} className="mb-6">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center shrink-0 mt-1">
                  <span className="text-sm font-medium text-brand-600">{user?.display_name?.[0]?.toUpperCase() || 'U'}</span>
                </div>
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddComment(e); }}
                    placeholder="Leave a comment or update... (Ctrl+Enter to submit)"
                    className="w-full min-h-[80px] p-3 text-sm bg-surface-50 border border-surface-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
                  />
                  <div className="flex justify-end mt-2">
                    <button type="submit" disabled={!newComment.trim() || submittingComment} className="btn btn-primary btn-sm">
                      {submittingComment ? 'Posting...' : 'Comment'}
                    </button>
                  </div>
                </div>
              </div>
            </form>
            
            <div className="space-y-0">
              {activities.length > 0 ? (
                activities.map((activity, idx) => (
                  <div key={activity.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        activity.activity_type === 'comment' ? 'bg-brand-100' :
                        activity.activity_type === 'created' ? 'bg-green-100' :
                        activity.activity_type === 'dependency_added' ? 'bg-amber-100' :
                        activity.activity_type === 'dependency_removed' ? 'bg-amber-100' : 'bg-surface-100'
                      }`}>
                        {activity.activity_type === 'comment' ? <HiChat className="w-4 h-4 text-brand-500" /> :
                         activity.activity_type === 'created' ? <HiCheckCircle className="w-4 h-4 text-green-500" /> :
                         activity.activity_type.startsWith('dependency') ? <HiLockClosed className="w-4 h-4 text-amber-500" /> :
                         <HiClock className="w-4 h-4 text-surface-400" />}
                      </div>
                      {idx < activities.length - 1 && <div className="w-px flex-1 bg-surface-200 mt-1" />}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-surface-800">{activity.user?.display_name || 'System'}</span>
                        <span className="text-surface-400">
                          {activity.activity_type === 'comment' ? 'commented' :
                           activity.activity_type === 'created' ? 'created this task' :
                           activity.activity_type === 'updated' ? 'updated this task' :
                           activity.activity_type === 'moved' ? 'moved this task' :
                           activity.activity_type === 'dependency_added' ? 'added a dependency' :
                           activity.activity_type === 'dependency_removed' ? 'removed a dependency' :
                           activity.activity_type}
                        </span>
                        <span className="text-xs text-surface-400 ml-auto">
                          {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      {activity.content && activity.activity_type === 'comment' && (
                        <div className="mt-2 text-sm text-surface-700 bg-surface-50 rounded-lg p-3 whitespace-pre-wrap border border-surface-100">
                          {activity.content}
                        </div>
                      )}
                      {activity.content && activity.activity_type !== 'comment' && (
                        <p className="mt-1 text-xs text-surface-500">{activity.content}</p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-surface-400 py-6 text-sm">No activity yet — be the first to comment!</p>
              )}
            </div>
          </div>
        </div>

        {/* ── Sidebar ── */}
        <div className="space-y-4">
          {/* Quick Actions */}
          <div className="flex gap-2">
            {task.status !== 'done' ? (
              <button onClick={() => handleStatusChange('done')} className="btn btn-primary flex-1">
                <HiCheckCircle className="w-4 h-4 mr-2" /> Mark Complete
              </button>
            ) : (
              <button onClick={() => handleStatusChange('todo')} className="btn btn-secondary flex-1">Reopen Task</button>
            )}
            <button onClick={handleDelete} className="btn btn-ghost text-red-500 hover:bg-red-50 hover:text-red-600" title="Delete task">
              <HiTrash className="w-4 h-4" />
            </button>
          </div>

          {/* Details */}
          <div className="card p-5 space-y-4">
            <h3 className="text-xs font-semibold text-surface-400 uppercase tracking-wider">Details</h3>
            
            <DetailRow label="Assignee">
              <DropdownButton
                open={showAssigneeMenu} onToggle={() => setShowAssigneeMenu(!showAssigneeMenu)} onClose={() => setShowAssigneeMenu(false)}
                trigger={
                  task.assignee ? (
                    <button className="flex items-center gap-2 hover:bg-surface-50 dark:hover:bg-surface-700 rounded-lg px-2 py-1 -mx-2 transition-colors">
                      <div className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 flex items-center justify-center text-xs font-medium">
                        {task.assignee.display_name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <span className="text-sm text-surface-700 dark:text-surface-300">{task.assignee.display_name}</span>
                      <HiChevronDown className="w-3.5 h-3.5 text-surface-400" />
                    </button>
                  ) : (
                    <button className="text-sm text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-700 rounded-lg px-2 py-1 -mx-2 transition-colors flex items-center gap-1">
                      <HiUser className="w-4 h-4" /> Unassigned <HiChevronDown className="w-3.5 h-3.5" />
                    </button>
                  )
                }
                menu={[
                  <button key="unassign" onClick={() => handleAssigneeChange(null)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-500 hover:bg-surface-50 dark:hover:bg-surface-700">
                    <HiX className="w-4 h-4" /> Unassign
                  </button>,
                  ...orgUsers.map(u => (
                    <button key={u.id} onClick={() => handleAssigneeChange(u.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-surface-50 dark:hover:bg-surface-700 ${task.assignee_id === u.id ? 'bg-surface-50 dark:bg-surface-700 font-medium' : ''}`}>
                      <div className="w-5 h-5 rounded-full bg-surface-200 dark:bg-surface-600 text-surface-600 dark:text-surface-300 flex items-center justify-center text-xs">
                        {u.display_name?.[0]?.toUpperCase() || '?'}
                      </div>
                      {u.display_name}
                      {task.assignee_id === u.id && <HiCheck className="w-4 h-4 ml-auto text-brand-500" />}
                    </button>
                  ))
                ]}
                align="right"
              />
            </DetailRow>

            <DetailRow label="Due Date">
              {editingDueDate ? (
                <div className="flex items-center gap-2">
                  <input type="date" value={dueDateValue} onChange={(e) => setDueDateValue(e.target.value)}
                    onBlur={handleDueDateSave} onKeyDown={(e) => { if (e.key === 'Enter') handleDueDateSave(); }}
                    className="text-sm px-2 py-1 border border-surface-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                    autoFocus />
                  <button onClick={() => { setDueDateValue(''); handleDueDateSave(); }} className="text-xs text-surface-400 hover:text-red-500">Clear</button>
                </div>
              ) : (
                <button onClick={() => setEditingDueDate(true)}
                  className={`text-sm flex items-center gap-1.5 hover:bg-surface-50 rounded-lg px-2 py-1 -mx-2 transition-colors ${
                    isOverdue ? 'text-red-600 font-medium' : task.due_date ? 'text-surface-700' : 'text-surface-400'
                  }`}>
                  <HiCalendar className="w-4 h-4" />
                  {task.due_date ? format(new Date(task.due_date), 'MMM d, yyyy') : 'Set due date'}
                </button>
              )}
            </DetailRow>

            <DetailRow label="Reporter">
              {task.reporter ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-surface-200 flex items-center justify-center text-xs">
                    {task.reporter.display_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <span className="text-sm text-surface-700">{task.reporter.display_name}</span>
                </div>
              ) : <span className="text-sm text-surface-400">Unset</span>}
            </DetailRow>

            <DetailRow label="Project">
              {project ? (
                <Link to={`/projects/${project.id}`} className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: project.color || '#3b82f6' }} />
                  {project.name}
                </Link>
              ) : <span className="text-sm text-surface-400">None</span>}
            </DetailRow>

            <DetailRow label="Created">
              <span className="text-sm text-surface-600">{format(new Date(task.created_at), 'MMM d, yyyy')}</span>
            </DetailRow>

            <DetailRow label="Updated">
              <span className="text-sm text-surface-600">{formatDistanceToNow(new Date(task.updated_at), { addSuffix: true })}</span>
            </DetailRow>

            {task.estimated_hours != null && (
              <DetailRow label="Estimate">
                <span className="text-sm text-surface-600">{task.estimated_hours}h</span>
              </DetailRow>
            )}
          </div>

          {/* Labels */}
          <div className="card p-5">
            <h3 className="text-xs font-semibold text-surface-400 uppercase tracking-wider mb-3">Labels</h3>
            <div className="flex flex-wrap gap-2">
              {task.labels && task.labels.length > 0 ? (
                task.labels.map((label, idx) => (
                  <span key={idx} className="px-2.5 py-1 text-xs font-medium bg-surface-100 text-surface-700 rounded-full">{label}</span>
                ))
              ) : (
                <span className="text-sm text-surface-400">No labels</span>
              )}
            </div>
          </div>

          {/* Dependencies */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-surface-400 uppercase tracking-wider flex items-center gap-1.5">
                <HiLockClosed className="w-3.5 h-3.5" />
                Dependencies
              </h3>
              <button 
                onClick={() => setShowAddDependency(!showAddDependency)} 
                className="text-sm text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1"
              >
                <HiPlus className="w-4 h-4" /> Add
              </button>
            </div>

            {/* Add Dependency search */}
            {showAddDependency && (
              <div className="mb-3 pb-3 border-b border-surface-100 dark:border-surface-700">
                <input
                  type="text"
                  value={depSearchQuery}
                  onChange={(e) => handleSearchDependency(e.target.value)}
                  placeholder="Search tasks to add as dependency..."
                  className="w-full text-sm px-3 py-2 border border-surface-200 dark:border-surface-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-surface-700"
                  autoFocus
                />
                {depSearching && (
                  <p className="text-xs text-surface-400 mt-1">Searching...</p>
                )}
                {depSearchResults.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                    {depSearchResults.map(t => (
                      <button
                        key={t.id}
                        onClick={() => handleAddDependency(t.id)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left hover:bg-surface-50 dark:hover:bg-surface-700 rounded-lg transition-colors"
                      >
                        <span className={`w-2 h-2 rounded-full shrink-0 ${
                          t.status === 'done' ? 'bg-green-500' :
                          t.status === 'in_progress' ? 'bg-blue-500' :
                          t.status === 'blocked' ? 'bg-red-500' :
                          'bg-gray-400'
                        }`} />
                        <span className="truncate">{t.title}</span>
                      </button>
                    ))}
                  </div>
                )}
                {depSearchQuery.length >= 2 && !depSearching && depSearchResults.length === 0 && (
                  <p className="text-xs text-surface-400 mt-1">No matching tasks found</p>
                )}
              </div>
            )}

            {/* Blocked By (dependencies) */}
            {(task.dependencies || []).length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-surface-400 font-medium mb-1.5">Blocked by</p>
                <div className="space-y-1">
                  {task.dependencies.map(dep => (
                    <div key={dep.id} className="flex items-center gap-2 group">
                      <HiLockClosed className="w-3.5 h-3.5 text-red-400 shrink-0" />
                      <Link
                        to={`/tasks/${dep.depends_on_task_id}`}
                        className={`flex-1 text-sm truncate hover:text-brand-600 transition-colors ${
                          dep.depends_on_task_status === 'done' ? 'line-through text-surface-400' : 'text-surface-700 dark:text-surface-300'
                        }`}
                      >
                        {dep.depends_on_task_title || 'Unknown task'}
                      </Link>
                      {dep.depends_on_task_status === 'done' && (
                        <HiCheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                      )}
                      <button
                        onClick={() => handleRemoveDependency(dep.id)}
                        className="opacity-0 group-hover:opacity-100 text-surface-400 hover:text-red-500 transition-all p-0.5"
                        title="Remove dependency"
                      >
                        <HiX className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Blocking (dependents) */}
            {(task.dependents || []).length > 0 && (
              <div>
                <p className="text-xs text-surface-400 font-medium mb-1.5">Blocking</p>
                <div className="space-y-1">
                  {task.dependents.map(dep => (
                    <div key={dep.id} className="flex items-center gap-2">
                      <HiArrowRight className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                      <Link
                        to={`/tasks/${dep.task_id}`}
                        className={`flex-1 text-sm truncate hover:text-brand-600 transition-colors ${
                          dep.task_status === 'done' ? 'line-through text-surface-400' : 'text-surface-700 dark:text-surface-300'
                        }`}
                      >
                        {dep.task_title || 'Unknown task'}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(task.dependencies || []).length === 0 && (task.dependents || []).length === 0 && !showAddDependency && (
              <p className="text-sm text-surface-400 text-center py-2">No dependencies</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function DetailRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-sm text-surface-500 shrink-0 pt-1">{label}</span>
      <div>{children}</div>
    </div>
  )
}

function DropdownButton({ open, onToggle, onClose, trigger, menu, align = 'left' }) {
  const items = Array.isArray(menu) ? menu : null
  return (
    <div className="relative">
      <div onClick={onToggle} className="cursor-pointer">{trigger}</div>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={onClose} />
          <div className={`absolute top-full mt-1 z-50 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl shadow-lg min-w-[200px] max-h-60 overflow-y-auto ${align === 'right' ? 'right-0' : 'left-0'}`}>
            <div className="py-1">{items || menu}</div>
          </div>
        </>
      )}
    </div>
  )
}
